import { Input, Select, Badge, Button, Form, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { showToastError } from 'src/utilities/errortoast';
import { useAuth } from '@frontegg/react';
import { showToastSuccess } from 'src/utilities/toast';
import { FronteggContext } from '@frontegg/rest-api';
import { useSyncEhrAppointmentsByOrg } from 'src/hooks/ehr/useSyncEhrAppointmentsByOrg';
import { usePushEhrCredentialsV2, useOnboardEhr } from 'src/hooks/ehr';
import {
  getAllOPOrganizationAction,
  getAllOpenDROrganizationAction,
} from 'src/modules/SuperAdmin/store/actions/organization.action';
import { getAllFronteggRoles } from 'src/modules/SuperAdmin/store/actions/practitioner.action';
import { listAllTemplatesAction } from 'src/modules/SuperAdmin/store/actions/prebuilttemplates.action';
import { axiosAdmin } from 'src/lib/axios';
import { analytics, api } from 'src/services';
import { useIsOP, useIsOpenDr } from 'src/hooks/misc';

const OP_EHR_SYSTEMS = ['office_practicum', 'office_practicum_push'];
const ONBOARDING_USER_EMAIL = 'api@insighthealth.ai';

// `4-ih-ai-scribe-fhir` -> `4`; falls back to realm name in the token URL.
const extractPracticeId = (clientId, tokenUrl) =>
  clientId?.match(/^(\d+)-/)?.[1] ||
  tokenUrl?.match(/\/realms\/(\d+)-/)?.[1] ||
  '';

// `https://security.op.healthcare/2/auth/realms/...` -> 2
const extractApiVersion = (tokenUrl) => {
  const match = tokenUrl?.match(/security\.op\.healthcare\/(\d+)\//);
  return match ? Number(match[1]) : undefined;
};

const InviteHqPractitioner = ({ isHqAccount = false }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { mutateAsync: syncEhrAppointmentsAsync } =
    useSyncEhrAppointmentsByOrg();
  const { hQOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );
  const isOP = useIsOP();
  const isOpenDr = useIsOpenDr();

  const { allTemplates } = useSelector((state) => state.preBuiltTemplatesState);

  const { fronteggRolesAll } = useSelector((state) => state?.doctorsState);

  useEffect(() => {
    // isHqAccount is false for super admin menu
    if (isOP || !isHqAccount) {
      dispatch(getAllOPOrganizationAction());
    } else if (isOpenDr) {
      dispatch(getAllOpenDROrganizationAction());
    }
  }, [dispatch, isOP, isOpenDr, isHqAccount]);

  useEffect(() => {
    dispatch(getAllFronteggRoles());
  }, [dispatch]);

  useEffect(() => {
    if (!isHqAccount) {
      dispatch(listAllTemplatesAction());
    }
  }, [dispatch, isHqAccount]);

  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [fronteggRoleOptions, setFronteggRoleOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateOrgModalVisible, setIsCreateOrgModalVisible] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);
  const [pendingOrgSelectionName, setPendingOrgSelectionName] = useState(null);

  const [form] = Form.useForm();
  const [createOrgForm] = Form.useForm();
  const orgNameValue = Form.useWatch('organizationName', createOrgForm);
  const opClientIdValue = Form.useWatch('opClientId', createOrgForm);
  const opClientSecretValue = Form.useWatch('opClientSecret', createOrgForm);
  const opTokenUrlValue = Form.useWatch('opTokenUrl', createOrgForm);

  const { pushCredentials } = usePushEhrCredentialsV2();
  const { onboardEhr } = useOnboardEhr();

  // Only the OP create-org path needs OP credentials. OpenDr does not.
  const isOPCreateOrg = isOP || !isHqAccount;
  const canCreateOrg =
    !!orgNameValue?.trim() &&
    (!isOPCreateOrg ||
      (!!opClientIdValue?.trim() &&
        !!opClientSecretValue?.trim() &&
        !!opTokenUrlValue?.trim()));

  useEffect(() => {
    if (hQOrganizationsList && Array.isArray(hQOrganizationsList)) {
      let dataArr = hQOrganizationsList
        .filter(
          (org) =>
            !orgSearchTerm ||
            org.org_name.toLowerCase().includes(orgSearchTerm.toLowerCase())
        )
        .map((key) => ({
          value: key?.org_uuid,
          label: key?.org_name,
          displayLabel: (
            <Badge
              count={key?.user_count}
              overflowCount={99}
              offset={[20, 7]}
              style={{ backgroundColor: '#52c41a' }}
              showZero={true}
            >
              <span className="mr-1">{key?.org_name}</span>
            </Badge>
          ),
          org_uuid: key?.org_uuid,
          org_id: key?.org_id,
        }));
      setOrganizationOptions(dataArr);
    }
  }, [hQOrganizationsList, orgSearchTerm]);

  useEffect(() => {
    if (fronteggRolesAll && Array.isArray(fronteggRolesAll)) {
      let dataArr = [];
      if (isOP || !isHqAccount) {
        dataArr = fronteggRolesAll
          .filter((role) => role?.name?.startsWith('OP'))
          .map((role) => ({
            value: role.id,
            label: role.name,
          }));
      } else if (isOpenDr) {
        dataArr = fronteggRolesAll
          .filter((role) => role?.name?.startsWith('OpenDr'))
          .map((role) => ({
            value: role.id,
            label: role.name,
          }));
      } else {
        dataArr = fronteggRolesAll.map((role) => ({
          value: role.id,
          label: role.name,
        }));
      }
      setFronteggRoleOptions(dataArr);
    }
  }, [fronteggRolesAll, isOP, isOpenDr, isHqAccount]);

  useEffect(() => {
    if (!isHqAccount && allTemplates && Array.isArray(allTemplates)) {
      const dataArr = allTemplates
        .filter(
          (template) =>
            template?.encounter_phase === 'in-visit' &&
            template?.name?.startsWith('OP')
        )
        .map((template) => ({
          value: template.template_id,
          label: template.name,
        }));
      setTemplateOptions(dataArr);
    } else {
      setTemplateOptions([]);
    }
  }, [allTemplates, isHqAccount]);

  useEffect(() => {
    if (pendingOrgSelectionName && hQOrganizationsList?.length > 0) {
      const newlyCreatedOrg = hQOrganizationsList.find(
        (org) => org.org_name === pendingOrgSelectionName
      );

      if (newlyCreatedOrg?.org_uuid) {
        form.setFieldsValue({ organizationUuid: newlyCreatedOrg.org_uuid });
        setOrgSearchTerm('');
        setPendingOrgSelectionName(null);
        console.log('Auto-selected:', newlyCreatedOrg.org_name);
      } else {
        console.warn(
          `Could not automatically select the newly created organization "${pendingOrgSelectionName}" after list update.`
        );
        setPendingOrgSelectionName(null);
      }
    }
  }, [hQOrganizationsList]);

  const handleInvitePractitioner = async () => {
    if (isHqAccount) {
      analytics.track(`Clicked Invite Practitioner`, {
        email: user?.email,
        hq_account: isOP ? 'OP' : 'OpenDr',
      });
    }
    const values = await form.validateFields();

    const token = FronteggContext.getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };

    setIsSubmitting(true);

    try {
      const selectedRole = fronteggRoleOptions.find(
        (role) => role.value === values?.role
      );

      if (!selectedRole) {
        showToastError('Selected role not found. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const payload = {
        org_uuid: values?.organizationUuid,
        frontegg_role: selectedRole.label,
        first_name: (values?.firstName || '').trim(),
        last_name: (values?.lastName || '').trim(),
        create_frontegg_user: true,
        op_superadmin_invite: isHqAccount && isOP,
        email: {
          email_text: values?.email,
          email_type: '',
        },
        ...(values?.fhirId && { ehr_id: values.fhirId }),
        ...(values?.defaultInVisitTemplate && {
          ambient_in_visit_template_id: values.defaultInVisitTemplate,
        }),
      };

      await axiosAdmin.post(`/practitioners`, payload, { headers });
      showToastSuccess('User invited successfully');

      if (isOP || !isHqAccount) {
        await dispatch(getAllOPOrganizationAction());
      } else if (isOpenDr) {
        await dispatch(getAllOpenDROrganizationAction());
      }

      if (isOP) {
        try {
          const ehrSettingsResponse = await api.get(
            `/v2/op_organization/ehr_settings`,
            {
              headers: {
                ...headers,
                'Impersonate-Org-UUID': `${values?.organizationUuid}`,
                'Headless-Mode': true,
              },
            }
          );
          const opEhrSettings = ehrSettingsResponse?.data?.ehr_settings;

          if (opEhrSettings?.ehr_system) {
            await syncEhrAppointmentsAsync({
              org_uuid: values?.organizationUuid,
              ehr: opEhrSettings.ehr_system,
            });
            showToastSuccess('EHR appointments synced successfully');
          }
        } catch (syncError) {
          console.log('Failed to sync EHR appointments', syncError);
        }
      }
      form.resetFields();
    } catch (err) {
      console.log('error: ', err);
      showToastError(
        err?.response?.data?.error ||
          'Failed to invite practitioner. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const showCreateOrgModal = (name) => {
    setIsCreateOrgModalVisible(true);
    setTimeout(() => {
      createOrgForm.setFieldsValue({ organizationName: name });
    }, 0);
  };

  const handleCancelCreateOrg = () => {
    setIsCreateOrgModalVisible(false);
    createOrgForm.resetFields();
  };

  const handleCreateOrganization = async () => {
    try {
      const values = await createOrgForm.validateFields();
      const orgName = values.organizationName;
      setIsCreatingOrg(true);

      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      };

      let requestUrl = '';
      if (isOP || !isHqAccount) {
        requestUrl = '/organizations/op';
      } else if (isOpenDr) {
        requestUrl = '/organizations/opendr';
      }

      if (requestUrl === '') {
        showToastError('Error occurred while creating organization');
        setIsCreatingOrg(false);
        setPendingOrgSelectionName(null);
        return;
      }

      const response = await axiosAdmin.post(
        requestUrl,
        { org_name: orgName },
        { headers }
      );

      showToastSuccess(`Organization "${orgName}" created successfully`);

      if (isOP || !isHqAccount) {
        await dispatch(getAllOPOrganizationAction());
      } else if (isOpenDr) {
        await dispatch(getAllOpenDROrganizationAction());
      }

      // For OP orgs, immediately persist credentials and trigger onboarding.
      // POST /organizations/op returns Organization.json() which includes
      // org_uuid at the top level.
      if (isOPCreateOrg) {
        const newOrgUuid = response.data?.org_uuid;
        const practiceId = extractPracticeId(
          values.opClientId,
          values.opTokenUrl
        );
        const apiVersion = extractApiVersion(values.opTokenUrl);

        if (!newOrgUuid) {
          showToastError(
            'Org created, but could not resolve its ID to save credentials. Please add credentials manually.'
          );
        } else if (!practiceId || !apiVersion) {
          showToastError(
            'Could not read Practice ID / API Version from Client ID and Token URL. Please add credentials manually.'
          );
        } else {
          try {
            const credentials = {
              client_id: values.opClientId.trim(),
              client_secret: values.opClientSecret.trim(),
              practice_id: practiceId,
              api_version: apiVersion,
            };
            await Promise.all(
              OP_EHR_SYSTEMS.map((ehr_system) =>
                pushCredentials({
                  org_uuid: newOrgUuid,
                  ehr_system,
                  credentials,
                })
              )
            );
            await onboardEhr({
              org_uuid: newOrgUuid,
              ehr_system: 'office_practicum',
              practice_id: practiceId,
              user_email: ONBOARDING_USER_EMAIL,
              timezone: 'UTC',
            });
            showToastSuccess('Office Practicum credentials saved & onboarded');
          } catch (credErr) {
            const msg =
              credErr?.response?.data?.message ||
              credErr?.message ||
              'Org created, but credential save / onboarding failed.';
            showToastError(msg);
          }
        }
      }

      handleCancelCreateOrg();
      setIsCreatingOrg(false);
      setPendingOrgSelectionName(orgName);
    } catch (err) {
      console.error('Failed to create organization:', err);
      const errorMessage =
        err?.response?.data?.message ||
        'Failed to create organization. Please try again.';
      showToastError(errorMessage);
      setIsCreatingOrg(false);
      setPendingOrgSelectionName(null);
    }
  };

  const organizationNotFoundContent = orgSearchTerm ? (
    <div className="p-2 text-center">
      No organization found matching &quot;{orgSearchTerm}&quot;.
      <br />
      <Button
        type="link"
        onClick={() => showCreateOrgModal(orgSearchTerm)}
        style={{ padding: 0, height: 'auto', lineHeight: 'inherit' }}
      >
        Create new organization: &quot;{orgSearchTerm}&quot;
      </Button>
    </div>
  ) : null;

  return (
    <div className="flex-1 pt-4">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col gap-3">
          <div className="text-lg font-bold font-sans pl-1">
            Invite Practitioner
          </div>
          <Form
            form={form}
            layout="vertical"
            className="space-y-4"
            autoComplete="off"
          >
            <div className="hidden" />
            <Form.Item
              label="Organization name"
              className="field-wrapper"
              name="organizationUuid"
              rules={[
                { required: true, message: 'Please select an organization' },
              ]}
            >
              <Select
                showSearch
                filterOption={false}
                onSearch={setOrgSearchTerm}
                className="field"
                placeholder="Search or create organization"
                notFoundContent={organizationNotFoundContent}
                options={organizationOptions.map((item) => ({
                  value: item.value,
                  label: item.displayLabel,
                  name: item.label,
                }))}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="Role"
              name="role"
              className="field-wrapper"
              rules={[{ required: true, message: 'Role is required' }]}
            >
              <Select
                optionFilterProp="label"
                className="field"
                placeholder="Search role"
                options={fronteggRoleOptions}
              />
            </Form.Item>
            <Form.Item
              label="First name"
              name="firstName"
              className="field-wrapper"
              rules={[
                { required: true, message: 'First name is required' },
                {
                  whitespace: true,
                  message: 'First name cannot be empty space',
                },
              ]}
            >
              <Input className="field" placeholder="Enter first name" />
            </Form.Item>
            <Form.Item
              label="Last name"
              name="lastName"
              className="field-wrapper"
              rules={[
                { required: true, message: 'Last name is required' },
                {
                  whitespace: true,
                  message: 'Last name cannot be empty space',
                },
              ]}
            >
              <Input className="field" placeholder="Enter last name" />
            </Form.Item>
            <Form.Item
              label="FHIR ID"
              name="fhirId"
              className="field-wrapper"
              dependencies={['role']}
              rules={[
                ({ getFieldValue }) => ({
                  required:
                    isHqAccount &&
                    isOP &&
                    !!getFieldValue('role') &&
                    fronteggRoleOptions.find(
                      (role) => role.value === getFieldValue('role')
                    )?.label !== 'OP Super Admin',
                  message: 'FHIR ID is required for this role.',
                }),
              ]}
            >
              <Input className="field" placeholder="Enter FHIR ID" />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              className="field-wrapper"
              rules={[{ required: true, type: 'email' }]}
            >
              <Input className="field" type="email" placeholder="Enter email" />
            </Form.Item>
            {!isHqAccount && (
              <Form.Item
                label="Default in-visit template"
                name="defaultInVisitTemplate"
                className="field-wrapper"
                rules={[
                  {
                    required: true,
                    message: 'Default in-visit template is required',
                  },
                ]}
              >
                <Select
                  optionFilterProp="label"
                  className="field"
                  placeholder="Search template"
                  options={templateOptions}
                />
              </Form.Item>
            )}
          </Form>
          <div className="flex items-center w-full justify-center mt-4">
            <Button
              type="primary"
              htmlType="submit"
              className="ih-button-primary"
              onClick={handleInvitePractitioner}
              loading={isSubmitting}
            >
              Invite Practitioner
            </Button>
          </div>
        </div>
      </div>
      <Modal
        title="Create New Organization"
        open={isCreateOrgModalVisible}
        onCancel={handleCancelCreateOrg}
        confirmLoading={isCreatingOrg}
        footer={
          <div className="flex justify-end gap-2 mt-8">
            <Button
              key="back"
              onClick={handleCancelCreateOrg}
              disabled={isCreatingOrg}
              className="ih-button-secondary"
            >
              Discard
            </Button>
            <Button
              key="submit"
              type="submit"
              loading={isCreatingOrg}
              onClick={handleCreateOrganization}
              disabled={isCreatingOrg || !canCreateOrg}
              className="ih-button-primary"
            >
              Create Organization
            </Button>
          </div>
        }
      >
        <Form form={createOrgForm} layout="vertical" autoComplete="off">
          <Form.Item
            name="organizationName"
            label="Organization name"
            rules={[
              { required: true, message: 'Organization name is required' },
              {
                whitespace: true,
                message: 'Organization name cannot be empty space',
              },
            ]}
          >
            <Input placeholder="Enter organization name" />
          </Form.Item>

          {isOPCreateOrg && (
            <>
              <div className="text-sm font-semibold text-gray-700 mb-2">
                Office Practicum credentials
              </div>
              <Form.Item
                name="opClientId"
                label="Client ID"
                rules={[
                  { required: true, message: 'Client ID is required' },
                  { whitespace: true, message: 'Client ID cannot be empty' },
                ]}
              >
                <Input placeholder="e.g. 4-ih-ai-scribe-fhir" />
              </Form.Item>
              <Form.Item
                name="opClientSecret"
                label="Client Secret"
                rules={[
                  { required: true, message: 'Client Secret is required' },
                  {
                    whitespace: true,
                    message: 'Client Secret cannot be empty',
                  },
                ]}
              >
                <Input.Password placeholder="Enter Client Secret" />
              </Form.Item>
              <Form.Item
                name="opTokenUrl"
                label="Token URL"
                rules={[
                  { required: true, message: 'Token URL is required' },
                  { whitespace: true, message: 'Token URL cannot be empty' },
                  {
                    pattern: /security\.op\.healthcare\/\d+\//,
                    message:
                      'Token URL must look like https://security.op.healthcare/<version>/auth/realms/<id>-OP-Realm/...',
                  },
                ]}
              >
                <Input placeholder="https://security.op.healthcare/2/auth/realms/4-OP-Realm/..." />
              </Form.Item>
              <div className="text-xs text-gray-500 -mt-2">
                Practice ID and API version are auto-derived from these values.
              </div>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default InviteHqPractitioner;
